import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookieBite, faShieldAlt, faCog, faEye } from '@fortawesome/free-solid-svg-icons';

function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <FontAwesomeIcon icon={faCookieBite} className="text-6xl text-orange-200" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Política de Cookies
            </h1>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Información sobre cómo utilizamos las cookies para mejorar tu experiencia en PetitPrime.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faShieldAlt} className="text-orange-200" />
                <span>Protección de privacidad</span>
              </div>
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCog} className="text-orange-200" />
                <span>Control total</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FontAwesomeIcon icon={faCookieBite} className="mr-3 text-orange-600" />
              1. ¿Qué son las Cookies?
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (ordenador,
              tablet o móvil) cuando visitas nuestro sitio web. Estas cookies nos ayudan a:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Recordar tus preferencias y configuraciones</li>
              <li>Mejorar la funcionalidad y rendimiento del sitio</li>
              <li>Analizar cómo utilizas nuestra plataforma</li>
              <li>Proporcionar contenido personalizado</li>
              <li>Garantizar la seguridad de tu cuenta</li>
            </ul>
            <p className="text-gray-600 leading-relaxed">
              Las cookies no contienen información personal identificable y no pueden acceder a archivos
              en tu dispositivo ni instalar software malicioso.
            </p>
          </section>

          {/* Types of Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">2. Tipos de Cookies que Utilizamos</h2>

            <div className="space-y-8">
              {/* Essential Cookies */}
              <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-red-600" />
                  Cookies Esenciales
                </h3>
                <p className="text-gray-600 mb-4">
                  Estas cookies son necesarias para el funcionamiento básico del sitio web y no pueden ser desactivadas.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Autenticación y sesión de usuario</li>
                  <li>Seguridad y protección contra ataques</li>
                  <li>Funcionalidades básicas de la plataforma</li>
                  <li>Preferencias de idioma y región</li>
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <FontAwesomeIcon icon={faEye} className="mr-2 text-blue-600" />
                  Cookies de Análisis
                </h3>
                <p className="text-gray-600 mb-4">
                  Nos ayudan a entender cómo utilizas nuestro sitio web para poder mejorarlo.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Google Analytics (análisis de tráfico)</li>
                  <li>Hotjar (análisis de comportamiento)</li>
                  <li>Estadísticas de uso de funcionalidades</li>
                  <li>Rendimiento y errores del sitio</li>
                </ul>
              </div>

              {/* Functional Cookies */}
              <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <FontAwesomeIcon icon={faCog} className="mr-2 text-green-600" />
                  Cookies Funcionales
                </h3>
                <p className="text-gray-600 mb-4">
                  Mejoran la funcionalidad y personalización de tu experiencia.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Preferencias de interfaz y diseño</li>
                  <li>Configuraciones de notificaciones</li>
                  <li>Datos de formularios guardados</li>
                  <li>Integración con redes sociales</li>
                </ul>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Cookies de Marketing
                </h3>
                <p className="text-gray-600 mb-4">
                  Se utilizan para mostrar anuncios relevantes y medir la efectividad de campañas.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Google Ads (anuncios personalizados)</li>
                  <li>Facebook Pixel (seguimiento de conversiones)</li>
                  <li>LinkedIn Insight (análisis de audiencia)</li>
                  <li>Remarketing y publicidad dirigida</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Third Party Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">3. Cookies de Terceros</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Algunos servicios de terceros pueden instalar cookies en tu dispositivo:
            </p>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Google Services</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Google Analytics</li>
                    <li>• Google Ads</li>
                    <li>• Google Fonts</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Social Media</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Facebook Pixel</li>
                    <li>• LinkedIn Insight</li>
                    <li>• Twitter Analytics</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Payment Services</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Stripe</li>
                    <li>• PayPal</li>
                    <li>• Bank integrations</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Support Tools</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Intercom</li>
                    <li>• Zendesk</li>
                    <li>• LiveChat</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Cookie Management */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">4. Gestión de Cookies</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-4">4.1 Configuración del Navegador</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Puedes controlar y gestionar las cookies a través de la configuración de tu navegador:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Chrome</h4>
                <p className="text-sm text-gray-600">Configuración → Privacidad y seguridad → Cookies</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Firefox</h4>
                <p className="text-sm text-gray-600">Opciones → Privacidad y seguridad → Cookies</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Safari</h4>
                <p className="text-sm text-gray-600">Preferencias → Privacidad → Cookies</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Edge</h4>
                <p className="text-sm text-gray-600">Configuración → Cookies y permisos del sitio</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-4">4.2 Panel de Control de Cookies</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              En nuestro sitio web puedes gestionar tus preferencias de cookies a través de nuestro
              panel de control integrado:
            </p>
            <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Activar/desactivar cookies por categoría</li>
                <li>Ver detalles de cada tipo de cookie</li>
                <li>Eliminar cookies existentes</li>
                <li>Guardar preferencias personalizadas</li>
              </ul>
            </div>
          </section>

          {/* Cookie Duration */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">5. Duración de las Cookies</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">Cookies de Sesión</h4>
                  <p className="text-gray-600">Se eliminan automáticamente al cerrar el navegador</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">Cookies Temporales</h4>
                  <p className="text-gray-600">Permanecen hasta 30 días en tu dispositivo</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">Cookies Persistentes</h4>
                  <p className="text-gray-600">Pueden permanecer hasta 2 años según su propósito</p>
                </div>
              </div>
            </div>
          </section>

          {/* Impact of Disabling */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">6. Impacto de Desactivar Cookies</h2>
            <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-500">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">⚠️ Consideraciones Importantes</h3>
              <p className="text-gray-600 mb-4">
                Si decides desactivar las cookies, ten en cuenta que:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Algunas funcionalidades del sitio pueden no funcionar correctamente</li>
                <li>Es posible que tengas que volver a introducir información repetidamente</li>
                <li>La experiencia de usuario puede verse afectada</li>
                <li>No podremos personalizar el contenido según tus preferencias</li>
                <li>Algunos servicios de terceros pueden no estar disponibles</li>
              </ul>
            </div>
          </section>

          {/* Updates to Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">7. Actualizaciones de esta Política</h2>
            <p className="text-gray-600 leading-relaxed">
              Esta Política de Cookies puede ser actualizada periódicamente para reflejar cambios en
              nuestras prácticas o por otros motivos operativos, legales o regulatorios. Te notificaremos
              cualquier cambio significativo a través de nuestro sitio web o por correo electrónico.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">8. Información de Contacto</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-600 mb-4">
                Si tienes preguntas sobre nuestra Política de Cookies:
              </p>
              <div className="space-y-2 text-gray-600">
                <p><strong>Email:</strong> privacy@petitstores.com</p>
                <p><strong>Teléfono:</strong> +34 900 123 456</p>
                <p><strong>Delegado de Protección de Datos:</strong> dpo@petitstores.com</p>
                <p><strong>Dirección:</strong> Barcelona, España</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              ¿Necesitas más información sobre privacidad?
            </h3>
            <p className="text-gray-600 mb-6">
              Consulta nuestra política de privacidad completa para más detalles sobre el tratamiento de datos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/privacy"
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
              >
                Ver Política de Privacidad
              </Link>
              <Link
                to="/terminos"
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
              >
                Ver Términos y Condiciones
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicyPage;
